/**
 * ป้ายกำกับ, โทนสี และช่วงวันที่เริ่มต้นของหน้าจอ "ข้อยกเว้นการรับของ" (D-168 B6)
 *
 * ไม่มี network call ในไฟล์นี้เลย — เพื่อให้ทดสอบกฎการแสดงผลได้โดยไม่ต้อง mock
 * `fetch` ตามแบบ `*View.ts` อื่นทุกไฟล์ในแอปนี้ (`goodsReceiptView.ts`,
 * `procurementView.ts`, `executiveSummary/summaryView.ts`)
 */

import type { StatusTone } from '../../desktop/StatusBadge';
import { can } from '../permissions/client';
import { INVENTORY_MODULE } from '../quality/types';
import type { PermissionsState } from '../permissions/types';
import {
  DISCREPANCY_STATUS_APPROVED,
  DISCREPANCY_STATUS_OPEN,
  DISCREPANCY_STATUS_PENDING_MANAGEMENT_APPROVAL,
  DISCREPANCY_STATUS_REJECTED,
  SUMMARY_DEFAULT_RANGE_DAYS,
  SUMMARY_MAX_RANGE_DAYS,
} from './types';

/* ------------------------------------------------------------- ป้ายกำกับ -- */

/**
 * ป้ายของชนิดข้อยกเว้น — "ขาด/เกิน/ส่งช้าหลังปิด" ตาม invariant ของ WO นี้
 *
 * ชนิดที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ" ด้วยเหตุผลเดียวกับ
 * `qualityStatusLabel()`: คิวนี้อ่าน `discrepancy_type` เป็นสตริงอะไรก็ได้ที่ไม่ว่าง
 * (parser ไม่กรองทิ้ง) ป้ายที่เดาผิดจึงต้องแพ้ป้ายที่ไม่สวยแต่สื่อความถูก
 */
export function discrepancyTypeLabel(type: string): string {
  switch (type) {
    case 'SHORT':
      return 'ขาด';
    case 'OVER':
      return 'เกิน';
    case 'LATE_DELIVERY':
      return 'ส่งช้าหลังปิด';
    default:
      return type;
  }
}

export function discrepancyTypeTone(type: string): StatusTone {
  switch (type) {
    case 'SHORT':
      return 'warning';
    case 'OVER':
      return 'info';
    case 'LATE_DELIVERY':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** แหล่งที่มา — "คนใน ERP / ระบบ Store" ตาม invariant ของ WO นี้ */
export function sourceLabel(source: string): string {
  switch (source) {
    case 'ERP_DESK':
      return 'คนใน ERP';
    case 'INTEGRATION':
      return 'ระบบ Store';
    default:
      return source;
  }
}

/** สถานะของแถว — ค่าเดียวกับ `DiscrepancyStatus` ฝั่งเซิร์ฟเวอร์ (D-091) */
export function discrepancyStatusLabel(status: string): string {
  switch (status) {
    case DISCREPANCY_STATUS_OPEN:
      return 'เปิดรอดำเนินการ';
    case DISCREPANCY_STATUS_PENDING_MANAGEMENT_APPROVAL:
      return 'รอผู้บริหารอนุมัติ';
    case DISCREPANCY_STATUS_APPROVED:
      return 'อนุมัติแล้ว';
    case DISCREPANCY_STATUS_REJECTED:
      return 'ถูกปฏิเสธ';
    default:
      return status;
  }
}

export function discrepancyStatusTone(status: string): StatusTone {
  switch (status) {
    case DISCREPANCY_STATUS_OPEN:
    case DISCREPANCY_STATUS_PENDING_MANAGEMENT_APPROVAL:
      return 'warning';
    case DISCREPANCY_STATUS_APPROVED:
      return 'success';
    case DISCREPANCY_STATUS_REJECTED:
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * ปุ่ม "อนุมัติ" แสดงเฉพาะแถวที่ยังไม่อนุมัติ — `OPEN`/`PENDING_MANAGEMENT_APPROVAL`
 * เท่านั้น (invariant ของ WO นี้) ตรงกับ `409
 * RECEIVING_DISCREPANCY_NOT_APPROVABLE` ที่เซิร์ฟเวอร์ตอบสำหรับสถานะอื่นทุกสถานะ
 */
export function isApprovableStatus(status: string): boolean {
  return (
    status === DISCREPANCY_STATUS_OPEN || status === DISCREPANCY_STATUS_PENDING_MANAGEMENT_APPROVAL
  );
}

/**
 * ปุ่ม "อนุมัติ" แสดงเฉพาะผู้ที่ถือ `INVENTORY` + `APPROVE` — grant จริงของเส้น
 * `POST /v1/receiving-discrepancies/{id}/approve` ไม่ใช่ `PROCUREMENT` ที่เปิด
 * หน้าจอนี้ (invariant: "ผู้ที่ดูได้อย่างเดียวไม่เห็นปุ่ม")
 */
export function canApprove(permissions: PermissionsState): boolean {
  return can(permissions, INVENTORY_MODULE, 'APPROVE');
}

/**
 * มูลค่า NUMERIC ที่เดินทางมาเป็นสตริง — `null` (ของ `LATE_DELIVERY`) แสดงเป็น
 * เส้นขีดโดยตั้งใจ ไม่ใช่ `0` ซึ่งจะอ่านว่า "ไม่มีผลต่อบัญชี" ทั้งที่ความหมายจริงคือ
 * "ไม่มีมูลค่าให้คำนวณสำหรับชนิดนี้เลย"
 */
export function valueLabel(value: string | null): string {
  return value ?? '—';
}

/** รหัสเหตุผล — ตาม `DISCREPANCY_REASON_CODES` ฝั่งเซิร์ฟเวอร์ (D-168) */
export function reasonCodeLabel(reasonCode: string | null): string {
  switch (reasonCode) {
    case null:
      return '—';
    case 'SUPPLIER_WILL_NOT_DELIVER':
      return 'ผู้ขายแจ้งว่าจะไม่ส่งส่วนที่เหลือ';
    case 'SUPPLIER_CANNOT_SUPPLY_REMAINDER':
      return 'ผู้ขายผลิต/จัดหาส่วนที่เหลือไม่ได้';
    case 'ORDER_NO_LONGER_NEEDED':
      return 'ไม่ต้องการของส่วนที่เหลือแล้ว';
    case 'QUALITY_REJECTED_AT_RECEIVING':
      return 'ถูกปฏิเสธคุณภาพตอนรับเข้า';
    case 'PRICE_OR_TERMS_DISPUTE':
      return 'ข้อพิพาทเรื่องราคา/เงื่อนไข';
    case 'OTHER':
      return 'อื่น ๆ';
    default:
      return reasonCode;
  }
}

/* ------------------------------------------------------------ ช่วงวันที่ -- */

/**
 * `toCalendarDate`/`defaultDateRange` — **คัดลอกมาจาก
 * `lib/executiveSummary/summaryView.ts` โดยตั้งใจ** ไม่ใช่ import ข้ามโดเมน:
 * `executiveSummary/**` อยู่นอก `write_scope` ของ WO นี้ และตัวเลขเริ่มต้นก็ต่างกัน
 * (90 วันของรายงานนี้ ไม่ใช่ 30 วันของ dashboard) เหตุผลเดียวกับที่
 * `goodsReceiptView.ts` คัดลอก `hasCrossPlantScope` ไว้ — ยกขึ้นเป็น helper กลาง
 * เป็นข้อเสนอให้ `erp-orchestrator` ไม่ใช่การตัดสินใจของหน้าจอนี้
 */
function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD` จาก `Date` — อ่านค่าปี/เดือน/วันตรง ๆ ไม่ผ่าน `toISOString()` ซึ่งเลื่อนวันได้ตาม timezone */
export function toCalendarDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export interface SummaryDateRange {
  readonly date_from: string;
  readonly date_to: string;
}

/** ช่วงเริ่มต้นของส่วนรายงานสรุป — 90 วันล่าสุดจนถึงวันนี้ (invariant ของ WO นี้) */
export function defaultSummaryDateRange(today: Date = new Date()): SummaryDateRange {
  const from = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (SUMMARY_DEFAULT_RANGE_DAYS - 1),
  );
  return { date_from: toCalendarDate(from), date_to: toCalendarDate(today) };
}

/** ช่วงกลับด้าน — `date_from` มาหลัง `date_to` — เทียบแบบสตริงได้เพราะเป็น `YYYY-MM-DD` ล้วน */
export function isReversedSummaryRange(dateFrom: string, dateTo: string): boolean {
  return dateFrom > dateTo;
}

/** จำนวนวันของช่วง แบบรวมวันแรกและวันสุดท้าย — คำนวณผ่าน UTC เพื่อไม่ให้ DST ของ browser ปนเข้ามา */
export function summaryRangeLengthDays(dateFrom: string, dateTo: string): number {
  const from = Date.UTC(
    Number(dateFrom.slice(0, 4)),
    Number(dateFrom.slice(5, 7)) - 1,
    Number(dateFrom.slice(8, 10)),
  );
  const to = Date.UTC(
    Number(dateTo.slice(0, 4)),
    Number(dateTo.slice(5, 7)) - 1,
    Number(dateTo.slice(8, 10)),
  );
  return Math.round((to - from) / 86_400_000) + 1;
}

/** ช่วงกว้างเกิน 366 วันที่ contract รับ — ใช้ปฏิเสธที่ฟอร์มก่อนคำขอจะออกเดินทาง */
export function summaryRangeExceedsMaxDays(dateFrom: string, dateTo: string): boolean {
  if (isReversedSummaryRange(dateFrom, dateTo)) {
    return false;
  }
  return summaryRangeLengthDays(dateFrom, dateTo) > SUMMARY_MAX_RANGE_DAYS;
}

/**
 * ช่วงที่ฟอร์มนี้ส่งคำขอได้ — ทั้งสองกฎรวมเป็นเงื่อนไขเดียว ใช้ปิดปุ่ม/ไม่ยิง effect
 * แทนที่จะปล่อยให้ผู้ใช้พบ `422` เป็นคำตอบแรกของทุกครั้งที่กรอกผิด
 */
export function isRequestableSummaryRange(dateFrom: string, dateTo: string): boolean {
  return !isReversedSummaryRange(dateFrom, dateTo) && !summaryRangeExceedsMaxDays(dateFrom, dateTo);
}
